import React from 'react';

/**
 * ROOT ERROR BOUNDARY
 * 
 * This is the top-level error boundary that catches fatal crashes.
 * 
 * CRITICAL: This fallback UI must NOT use any React hooks or context!
 * It renders OUTSIDE most providers when an error occurs.
 * 
 * Keep this component minimal - just buttons and text.
 */

interface Props { 
  children: React.ReactNode 
}

interface State { 
  hasError: boolean; 
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

const isDev = import.meta.env.DEV;

export class RootErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log in dev, but also send to console in prod for debugging
    console.error('❌ RootErrorBoundary caught fatal error:', error.message);
    if (isDev) {
      console.error('Full error:', error);
      console.error('Component stack:', errorInfo.componentStack);
    }
    this.setState({ errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleClearAndReload = () => {
    // Clear potentially corrupted state
    try {
      localStorage.removeItem('supabase.auth.token');
      sessionStorage.clear();
    } catch (e) {
      // Ignore storage errors
    }
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // MINIMAL FALLBACK - No hooks, no context, no complex components
      // Uses inline styles to avoid any CSS-in-JS issues
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          backgroundColor: '#fafafa',
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }}>
          <div style={{
            maxWidth: '400px',
            width: '100%',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>🛠️</div>
            <h1 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#1a1a1a',
              marginBottom: '12px'
            }}>
              Something went wrong
            </h1>
            <p style={{
              color: '#666',
              marginBottom: '24px',
              lineHeight: 1.5
            }}>
              We encountered an unexpected error. Please try refreshing the page.
            </p>
            
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <button
                onClick={this.handleReload}
                style={{
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: '500',
                  color: 'white',
                  backgroundColor: '#2563eb',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Refresh Page
              </button>
              <button
                onClick={this.handleGoHome}
                style={{
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: '500',
                  color: '#374151',
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Go to Home
              </button>
              <button
                onClick={this.handleClearAndReload}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  color: '#6b7280',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                Clear cache & reload
              </button>
            </div>
            
            {/* Only show technical details in development */}
            {isDev && this.state.error && (
              <details style={{
                marginTop: '24px',
                padding: '16px',
                backgroundColor: '#fef2f2',
                borderRadius: '8px',
                textAlign: 'left',
                fontSize: '12px'
              }}>
                <summary style={{ cursor: 'pointer', fontWeight: '500', marginBottom: '8px' }}>
                  Developer info (dev only)
                </summary>
                <pre style={{
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  color: '#dc2626'
                }}>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
